import { ExternalLink, Edit2, Trash2, Star, BarChart3, Layout, Globe, MoreHorizontal, Archive, ArchiveRestore } from 'lucide-react';
import Checkbox from './Checkbox';
import Badge from './Badge';

// Helper to normalize assignment value (handles both old string format and new object format)
function normalizeAssignment(value) {
  if (typeof value === 'string') {
    return { frequency: value, scheduledDay: null };
  }
  return { frequency: value?.frequency || 'daily', scheduledDay: value?.scheduledDay ?? null };
}

function getAssignments(item) {
  if (item.assignments) {
    return item.assignments;
  }
  if (item.owner && item.frequency) {
    return { [item.owner]: item.frequency };
  }
  return {};
}

// Get frequency for a specific user (handles both formats)
function getUserFrequency(item, userName) {
  const assignments = getAssignments(item);
  const assignment = assignments[userName];
  if (!assignment) return null;
  return normalizeAssignment(assignment).frequency;
}

// Get scheduled day for a specific user (handles both formats)
function getUserScheduledDay(item, userName) {
  const assignments = getAssignments(item);
  const assignment = assignments[userName];
  if (!assignment) return null;
  return normalizeAssignment(assignment).scheduledDay;
}

function formatAssignmentBadges(assignments, viewingUserId = null) {
  const userIds = Object.keys(assignments);

  if (userIds.length === 0) return null;

  // Helper to get frequency from assignment (handles both formats)
  const getFreq = (assignment) => normalizeAssignment(assignment).frequency;

  // If viewing a specific user, only show their frequency
  if (viewingUserId && assignments[viewingUserId]) {
    const freq = getFreq(assignments[viewingUserId]);
    return [{ text: freq, variant: freq }];
  }

  const freq0 = getFreq(assignments[userIds[0]]);
  const freq1 = userIds[1] ? getFreq(assignments[userIds[1]]) : null;

  // If all users have the same frequency, show it once with "(All)"
  if (userIds.length === 2 && freq0 === freq1) {
    return [{ text: `${freq0} (All)`, variant: freq0 }];
  }

  // Otherwise just show the frequencies without user names (UIDs aren't user-friendly)
  const uniqueFreqs = [...new Set(userIds.map(uid => getFreq(assignments[uid])))];
  return uniqueFreqs.map(freq => ({
    text: freq,
    variant: freq
  }));
}

const typeIcons = {
  dashboard: { icon: BarChart3, color: 'text-accent' },
  tracker: { icon: Layout, color: 'text-text-tertiary' },
  webapp: { icon: Globe, color: 'text-weekly' },
  other: { icon: MoreHorizontal, color: 'text-text-muted' }
};

export default function LinkItem({
  item,
  checked,
  onToggleCheck,
  onTogglePin,
  onEdit,
  onDelete,
  onArchive,
  onUnarchive,
  isArchived = false,
  showFrequency = true,
  showPin = true,
  viewingUserId = null,
  isAdmin = true
}) {
  const assignments = getAssignments(item);
  const assignmentBadges = showFrequency ? formatAssignmentBadges(assignments, viewingUserId) : null;
  const TypeIcon = typeIcons[item.type]?.icon;
  const typeColor = typeIcons[item.type]?.color;

  return (
    <div
      className={`
        group relative flex items-center gap-2.5 p-2.5
        bg-surface-card/60 hover:bg-surface-card
        rounded-lg border transition-all duration-200
        ${item.pinned
          ? 'border-accent/30 bg-gradient-to-r from-accent-muted/30 to-transparent'
          : 'border-border hover:border-border-strong'
        }
        ${checked ? 'opacity-60' : ''}
      `}
    >
      {item.pinned && (
        <div className="absolute left-0 top-2 bottom-2 w-0.5 bg-gradient-to-b from-accent to-orange-hover rounded-full" />
      )}

      {onToggleCheck && (
        <Checkbox checked={checked} onChange={onToggleCheck} />
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`
              font-medium text-xs text-text-primary
              hover:text-accent transition-colors
              ${checked ? 'line-through text-text-tertiary' : ''}
            `}
          >
            {item.name}
          </a>

          {TypeIcon && (
            <TypeIcon size={12} className={typeColor} />
          )}

          {item.pinned && (
            <Star size={10} className="text-accent fill-accent" />
          )}
        </div>

        <div className="flex gap-1 flex-wrap">
          {assignmentBadges && assignmentBadges.map((badge, idx) => (
            <Badge key={idx} variant={badge.variant}>{badge.text}</Badge>
          ))}
          {item.category && <Badge variant="neutral">{item.category}</Badge>}
        </div>
      </div>

      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        {isAdmin && showPin && onTogglePin && (
          <button
            onClick={onTogglePin}
            className={`
              p-1.5 rounded-md border-none cursor-pointer flex items-center transition-all duration-150
              ${item.pinned
                ? 'bg-accent-muted text-accent'
                : 'bg-transparent text-text-muted hover:bg-surface-hover hover:text-text-secondary'
              }
            `}
            title={item.pinned ? 'Unpin' : 'Pin'}
          >
            <Star size={12} fill={item.pinned ? 'currentColor' : 'none'} />
          </button>
        )}

        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 rounded-md bg-surface-hover text-text-secondary flex items-center justify-center no-underline transition-all duration-150 hover:bg-accent-muted hover:text-accent"
        >
          <ExternalLink size={12} />
        </a>

        {isAdmin && onEdit && (
          <button
            onClick={onEdit}
            className="p-1.5 rounded-md bg-transparent border-none text-text-muted cursor-pointer flex items-center hover:bg-surface-hover hover:text-text-secondary transition-all duration-150"
          >
            <Edit2 size={12} />
          </button>
        )}

        {isAdmin && isArchived && onUnarchive && (
          <button
            onClick={onUnarchive}
            className="p-1.5 rounded-md bg-transparent border-none text-text-muted cursor-pointer flex items-center hover:bg-green-500/10 hover:text-green-400 transition-all duration-150"
            title="Unarchive"
          >
            <ArchiveRestore size={12} />
          </button>
        )}

        {isAdmin && !isArchived && onArchive && (
          <button
            onClick={onArchive}
            className="p-1.5 rounded-md bg-transparent border-none text-text-muted cursor-pointer flex items-center hover:bg-amber-500/10 hover:text-amber-400 transition-all duration-150"
            title="Archive"
          >
            <Archive size={12} />
          </button>
        )}

        {isAdmin && isArchived && onDelete && (
          <button
            onClick={onDelete}
            className="p-1.5 rounded-md bg-transparent border-none text-text-muted cursor-pointer flex items-center hover:bg-red-500/10 hover:text-red-400 transition-all duration-150"
            title="Delete permanently"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="md:hidden p-1.5 rounded-md bg-surface-hover text-text-secondary flex items-center justify-center no-underline"
      >
        <ExternalLink size={14} />
      </a>
    </div>
  );
}

export { getAssignments, getUserFrequency, getUserScheduledDay, normalizeAssignment };
